'use client';

import { useEffect, useRef, useState } from 'react';

export const useIsScrolledFromTop = (onScroll?: () => void): { isScrolledFromTop: boolean } => {
  const threshold = 50;
  const headerThreshold = 80;
  const animationDelay = 300;

  const [isScrolledFromTop, setIsScrolledFromTop] = useState(false);

  const isScrolledRef = useRef(false);
  const scrollDirectionRef = useRef<'up' | 'down' | null>(null);
  const tickingRef = useRef(false);
  const lastScrollYRef = useRef(0);
  const scrollListenerActiveRef = useRef(false);

  useEffect(() => {
    const initialScrollY = window.pageYOffset;
    const initialScrolled = initialScrollY > headerThreshold;

    lastScrollYRef.current = initialScrollY;
    isScrolledRef.current = initialScrolled;

    if (initialScrolled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsScrolledFromTop(true);
    }

    const updateScrollDirection = () => {
      if (!scrollListenerActiveRef.current) {
        tickingRef.current = false;
        return;
      }

      const scrollY = window.pageYOffset;

      if (scrollDirectionRef.current === 'down' || scrollDirectionRef.current === null) {
        if (scrollY > headerThreshold && !isScrolledRef.current) {
          isScrolledRef.current = true;
          setIsScrolledFromTop(true);
        }
      } else if (scrollDirectionRef.current === 'up') {
        if (scrollY === 0 && isScrolledRef.current) {
          isScrolledRef.current = false;
          setIsScrolledFromTop(false);
        }
      }

      if (Math.abs(scrollY - lastScrollYRef.current) < threshold) {
        tickingRef.current = false;
        return;
      }

      scrollDirectionRef.current = scrollY > lastScrollYRef.current ? 'down' : 'up';
      lastScrollYRef.current = scrollY > 0 ? scrollY : 0;
      tickingRef.current = false;

      if (scrollListenerActiveRef.current) {
        onScroll?.();
      }
    };

    const handleScroll = () => {
      if (!tickingRef.current) {
        requestAnimationFrame(updateScrollDirection);
        tickingRef.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    scrollListenerActiveRef.current = false;

    const timeoutId = setTimeout(() => {
      scrollListenerActiveRef.current = true;
    }, animationDelay);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, [onScroll]);

  return { isScrolledFromTop };
};
